import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { LoginPage } from './auth/LoginPage';
import { PublicOnly, RequireAuth } from './auth/RequireAuth';
import { NotFound } from './pages/NotFound';
import { Spinner } from './components/Spinner';

/**
 * Route-level code splitting (item 9.5).
 *
 * Why: every authenticated screen is loaded on demand via {@link lazy} so the
 * initial JavaScript bundle only carries the shell, the auth/login flow and the
 * routing table. Each page below becomes its own Vite chunk that the browser
 * fetches the first time its route is visited, which keeps the entry chunk small
 * and the first paint fast (see docs/engineering/frontend-performance.md).
 *
 * What stays eager (imported at the top): the router, the {@link AppShell}, the
 * public {@link LoginPage}/{@link SsoCallbackPage}, the auth guards, and the tiny
 * {@link NotFound}/{@link PlaceholderPage} fallbacks — all of which are needed
 * immediately on first load and would only add extra round-trips if split out.
 *
 * The named-export → default-export bridge (`.then((m) => ({ default: m.X }))`)
 * is the type-safe way to feed {@link lazy}, which requires a default export;
 * TypeScript infers each component's type from the module, so no casts are needed.
 */
// The authenticated shell is lazy: the /login entry path doesn't need it, so it
// stays out of the entry chunk (item 9.5 budget). It loads once, right after auth.
const AppShell = lazy(() => import('./shell/AppShell').then((m) => ({ default: m.AppShell })));
// Public auth pages are lazy too (rare paths, kept out of the entry chunk to
// protect the initial-payload budget — item 9.5). Only /login stays eager.
const SsoCallbackPage = lazy(() =>
  import('./auth/SsoCallbackPage').then((m) => ({ default: m.SsoCallbackPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const HelpCentre = lazy(() => import('./help/HelpCentre').then((m) => ({ default: m.HelpCentre })));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const MonitoringPage = lazy(() =>
  import('./pages/monitoring/MonitoringPage').then((m) => ({ default: m.MonitoringPage })),
);
const AdministrationPage = lazy(() =>
  import('./pages/admin/AdministrationPage').then((m) => ({ default: m.AdministrationPage })),
);
const UsersPage = lazy(() => import('./pages/admin/UsersPage').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('./pages/admin/RolesPage').then((m) => ({ default: m.RolesPage })));
const FeatureFlagsPage = lazy(() =>
  import('./pages/admin/FeatureFlagsPage').then((m) => ({ default: m.FeatureFlagsPage })),
);
const ApproverAssignmentsPage = lazy(() =>
  import('./pages/admin/ApproverAssignmentsPage').then((m) => ({ default: m.ApproverAssignmentsPage })),
);
const NotificationSettingsPage = lazy(() =>
  import('./pages/admin/NotificationSettingsPage').then((m) => ({ default: m.NotificationSettingsPage })),
);
const BackupsPage = lazy(() => import('./pages/admin/BackupsPage').then((m) => ({ default: m.BackupsPage })));
const SsoSettingsPage = lazy(() => import('./pages/admin/SsoSettingsPage').then((m) => ({ default: m.SsoSettingsPage })));
const CustomersPage = lazy(() =>
  import('./pages/customers/CustomersPage').then((m) => ({ default: m.CustomersPage })),
);
const CustomerDetailPage = lazy(() =>
  import('./pages/customers/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })),
);
const ApplicationsPage = lazy(() =>
  import('./pages/applications/ApplicationsPage').then((m) => ({ default: m.ApplicationsPage })),
);
const ApplicationDetailPage = lazy(() =>
  import('./pages/applications/ApplicationDetailPage').then((m) => ({ default: m.ApplicationDetailPage })),
);
const EnvironmentsPage = lazy(() =>
  import('./pages/environments/EnvironmentsPage').then((m) => ({ default: m.EnvironmentsPage })),
);
const EnvironmentDetailPage = lazy(() =>
  import('./pages/environments/EnvironmentDetailPage').then((m) => ({ default: m.EnvironmentDetailPage })),
);
const EnvironmentConfigPage = lazy(() =>
  import('./pages/config/EnvironmentConfigPage').then((m) => ({ default: m.EnvironmentConfigPage })),
);
const ConfigVersionsPage = lazy(() =>
  import('./pages/config/ConfigVersionsPage').then((m) => ({ default: m.ConfigVersionsPage })),
);
const EnvironmentHealthPage = lazy(() =>
  import('./pages/health/EnvironmentHealthPage').then((m) => ({ default: m.EnvironmentHealthPage })),
);
const EnvironmentReconciliationPage = lazy(() =>
  import('./pages/reconciliation/EnvironmentReconciliationPage').then((m) => ({
    default: m.EnvironmentReconciliationPage,
  })),
);
const CredentialsPage = lazy(() =>
  import('./pages/credentials/CredentialsPage').then((m) => ({ default: m.CredentialsPage })),
);
const CredentialDetailPage = lazy(() =>
  import('./pages/credentials/CredentialDetailPage').then((m) => ({ default: m.CredentialDetailPage })),
);
const AgentsPage = lazy(() => import('./pages/agents/AgentsPage').then((m) => ({ default: m.AgentsPage })));
const AgentDetailPage = lazy(() =>
  import('./pages/agents/AgentDetailPage').then((m) => ({ default: m.AgentDetailPage })),
);
const OnboardingWizardPage = lazy(() =>
  import('./pages/onboarding/OnboardingWizardPage').then((m) => ({ default: m.OnboardingWizardPage })),
);
const ReleasesPage = lazy(() => import('./pages/releases/ReleasesPage').then((m) => ({ default: m.ReleasesPage })));
const ReleaseDetailPage = lazy(() =>
  import('./pages/releases/ReleaseDetailPage').then((m) => ({ default: m.ReleaseDetailPage })),
);
const GovernancePage = lazy(() =>
  import('./pages/governance/GovernancePage').then((m) => ({ default: m.GovernancePage })),
);
const DeploymentWindowsPage = lazy(() =>
  import('./pages/governance/DeploymentWindowsPage').then((m) => ({ default: m.DeploymentWindowsPage })),
);
const FreezesPage = lazy(() => import('./pages/governance/FreezesPage').then((m) => ({ default: m.FreezesPage })));
const GuardrailPreviewPage = lazy(() =>
  import('./pages/governance/GuardrailPreviewPage').then((m) => ({ default: m.GuardrailPreviewPage })),
);
const CompatibilityMatrixPage = lazy(() =>
  import('./pages/compatibility/CompatibilityMatrixPage').then((m) => ({ default: m.CompatibilityMatrixPage })),
);
const CompatibilityPreviewPage = lazy(() =>
  import('./pages/compatibility/CompatibilityPreviewPage').then((m) => ({ default: m.CompatibilityPreviewPage })),
);
const ApprovalsPage = lazy(() =>
  import('./pages/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })),
);
const ApprovalDetailPage = lazy(() =>
  import('./pages/approvals/ApprovalDetailPage').then((m) => ({ default: m.ApprovalDetailPage })),
);
const PromotionsPage = lazy(() =>
  import('./pages/promotions/PromotionsPage').then((m) => ({ default: m.PromotionsPage })),
);
const PromotionRequestPage = lazy(() =>
  import('./pages/promotions/PromotionRequestPage').then((m) => ({ default: m.PromotionRequestPage })),
);
const PromotionDetailPage = lazy(() =>
  import('./pages/promotions/PromotionDetailPage').then((m) => ({ default: m.PromotionDetailPage })),
);
const DeploymentsPage = lazy(() =>
  import('./pages/deployments/DeploymentsPage').then((m) => ({ default: m.DeploymentsPage })),
);
const DeploymentDetailPage = lazy(() =>
  import('./pages/deployments/DeploymentDetailPage').then((m) => ({ default: m.DeploymentDetailPage })),
);
const ProvisioningRunsPage = lazy(() =>
  import('./pages/provisioning/ProvisioningRunsPage').then((m) => ({ default: m.ProvisioningRunsPage })),
);
const ProvisioningRunDetailPage = lazy(() =>
  import('./pages/provisioning/ProvisioningRunDetailPage').then((m) => ({ default: m.ProvisioningRunDetailPage })),
);
const TemplatesPage = lazy(() =>
  import('./pages/templates/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
const TemplateDetailPage = lazy(() =>
  import('./pages/templates/TemplateDetailPage').then((m) => ({ default: m.TemplateDetailPage })),
);
const TemplateWizardPage = lazy(() =>
  import('./pages/templates/TemplateWizardPage').then((m) => ({ default: m.TemplateWizardPage })),
);
const SecurityPage = lazy(() =>
  import('./pages/security/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);
const AccountSecurityPage = lazy(() =>
  import('./pages/account/AccountSecurityPage').then((m) => ({ default: m.AccountSecurityPage })),
);
const DeploymentPoliciesPage = lazy(() =>
  import('./pages/governance/DeploymentPoliciesPage').then((m) => ({ default: m.DeploymentPoliciesPage })),
);
const EnvironmentTypesPage = lazy(() =>
  import('./pages/admin/EnvironmentTypesPage').then((m) => ({ default: m.EnvironmentTypesPage })),
);
const IntegrationsPage = lazy(() =>
  import('./pages/admin/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })),
);
const AiSettingsPage = lazy(() =>
  import('./pages/admin/AiSettingsPage').then((m) => ({ default: m.AiSettingsPage })),
);
const SetupPresetsPage = lazy(() =>
  import('./pages/admin/SetupPresetsPage').then((m) => ({ default: m.SetupPresetsPage })),
);
const RolloutsPage = lazy(() =>
  import('./pages/rollouts/RolloutsPage').then((m) => ({ default: m.RolloutsPage })),
);
const RolloutWizardPage = lazy(() =>
  import('./pages/rollouts/RolloutWizardPage').then((m) => ({ default: m.RolloutWizardPage })),
);
const RolloutDetailPage = lazy(() =>
  import('./pages/rollouts/RolloutDetailPage').then((m) => ({ default: m.RolloutDetailPage })),
);
const PipelineWizardPage = lazy(() =>
  import('./pages/pipelines/PipelineWizardPage').then((m) => ({ default: m.PipelineWizardPage })),
);
const PipelinesPage = lazy(() =>
  import('./pages/pipelines/PipelinesPage').then((m) => ({ default: m.PipelinesPage })),
);
const PipelineDetailPage = lazy(() =>
  import('./pages/pipelines/PipelineDetailPage').then((m) => ({ default: m.PipelineDetailPage })),
);
const PipelineRunPage = lazy(() =>
  import('./pages/pipelines/PipelineRunPage').then((m) => ({ default: m.PipelineRunPage })),
);
const NotificationDestinationsPage = lazy(() =>
  import('./pages/admin/NotificationDestinationsPage').then((m) => ({ default: m.NotificationDestinationsPage })),
);

/**
 * Content-area fallback shown while a lazy route chunk is fetching.
 *
 * Kept intentionally light — a single centred {@link Spinner} — so it renders
 * instantly from the entry bundle and never causes layout shift (it fills the
 * routed content region below the persistent {@link AppShell} chrome).
 */
function RouteFallback(): JSX.Element {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" role="status" aria-live="polite">
      <Spinner label="Loading…" />
    </div>
  );
}

/**
 * Pathless layout route that scopes the {@link Suspense} boundary to the routed
 * content area. Because it sits *below* the {@link AppShell} in the tree, the
 * shell chrome (sidebar, header) stays mounted while a page chunk loads — only
 * the content region swaps to {@link RouteFallback}, avoiding a full-screen flash
 * on every navigation.
 */
function SuspendedOutlet(): JSX.Element {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  );
}

/**
 * Route table (spec 08 §3). `/login` is public; everything else is behind
 * {@link RequireAuth} and rendered inside the {@link AppShell}. Extracted from
 * {@link App} (which owns the Router) so tests can mount it under a MemoryRouter.
 *
 * All authenticated screens are lazy-loaded (item 9.5); see the module header for
 * the code-splitting rationale.
 */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      {/* SSO callback — public; reads the token from the URL fragment (item 7.7). */}
      <Route
        path="/login/sso/callback"
        element={
          <Suspense fallback={<RouteFallback />}>
            <SsoCallbackPage />
          </Suspense>
        }
      />
      {/* Public password-reset flow (master §85) — resolves even with a stale session. */}
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<RouteFallback />}>
            <ForgotPasswordPage />
          </Suspense>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<RouteFallback />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />
      <Route
        element={
          <RequireAuth>
            <Suspense fallback={<RouteFallback />}>
              <AppShell />
            </Suspense>
          </RequireAuth>
        }
      >
        {/* Suspense boundary lives here so the shell survives chunk loads. */}
        <Route element={<SuspendedOutlet />}>
          <Route index element={<Dashboard />} />
          <Route path="help" element={<HelpCentre />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="monitoring" element={<MonitoringPage />} />
          <Route path="administration" element={<AdministrationPage />}>
            <Route index element={<UsersPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="feature-flags" element={<FeatureFlagsPage />} />
            <Route path="approvers" element={<ApproverAssignmentsPage />} />
            <Route path="notifications" element={<NotificationSettingsPage />} />
            <Route path="backups" element={<BackupsPage />} />
            <Route path="sso" element={<SsoSettingsPage />} />
            <Route path="environment-types" element={<EnvironmentTypesPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="notification-destinations" element={<NotificationDestinationsPage />} />
            <Route path="ai" element={<AiSettingsPage />} />
            <Route path="setup-presets" element={<SetupPresetsPage />} />
          </Route>
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:id" element={<CustomerDetailPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
          <Route path="applications/:id" element={<ApplicationDetailPage />} />
          <Route path="environments" element={<EnvironmentsPage />} />
          <Route path="environments/:id" element={<EnvironmentDetailPage />} />
          <Route path="environments/:id/config" element={<EnvironmentConfigPage />} />
          <Route path="environments/:id/health" element={<EnvironmentHealthPage />} />
          <Route path="environments/:id/reconciliation" element={<EnvironmentReconciliationPage />} />
          <Route path="environments/:id/config/versions" element={<ConfigVersionsPage />} />
          <Route path="credentials" element={<CredentialsPage />} />
          <Route path="credentials/:id" element={<CredentialDetailPage />} />
          <Route path="agents" element={<AgentsPage />} />
          <Route path="agents/:id" element={<AgentDetailPage />} />
          <Route path="onboarding" element={<OnboardingWizardPage />} />
          <Route path="environments/:id/onboard" element={<OnboardingWizardPage />} />
          <Route path="releases" element={<ReleasesPage />} />
          <Route path="releases/:id" element={<ReleaseDetailPage />} />
          <Route path="governance" element={<GovernancePage />}>
            <Route index element={<DeploymentWindowsPage />} />
            <Route path="freezes" element={<FreezesPage />} />
            <Route path="preview" element={<GuardrailPreviewPage />} />
            <Route path="policies" element={<DeploymentPoliciesPage />} />
          </Route>
          <Route path="compatibility" element={<CompatibilityMatrixPage />} />
          <Route path="compatibility/preview" element={<CompatibilityPreviewPage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="approvals/:id" element={<ApprovalDetailPage />} />
          <Route path="promotions" element={<PromotionsPage />} />
          <Route path="promotions/new" element={<PromotionRequestPage />} />
          <Route path="promotions/:id" element={<PromotionDetailPage />} />
          <Route path="deployments" element={<DeploymentsPage />} />
          <Route path="deployments/:id" element={<DeploymentDetailPage />} />
          <Route path="rollouts" element={<RolloutsPage />} />
          <Route path="rollouts/new" element={<RolloutWizardPage />} />
          <Route path="rollouts/:id" element={<RolloutDetailPage />} />
          <Route path="pipelines" element={<PipelinesPage />} />
          <Route path="pipelines/new" element={<PipelineWizardPage />} />
          <Route path="pipelines/:id" element={<PipelineDetailPage />} />
          <Route path="pipelines/:id/edit" element={<PipelineWizardPage />} />
          <Route path="pipelines/:id/runs/:runId" element={<PipelineRunPage />} />
          <Route path="security" element={<SecurityPage />} />
          {/* Per-user self-service account security (MFA) — reachable from the
              top-bar account menu, not the primary nav (spec 06 §2.1). */}
          <Route path="account/security" element={<AccountSecurityPage />} />
          <Route path="provisioning" element={<ProvisioningRunsPage />} />
          {/* Retired routed wizard (spec 15 §10.4) — the guided setup modal now
              opens from the Customers page; this keeps every existing link
              (nav button, help articles, the page tour) working unchanged. */}
          <Route path="provisioning/new-customer" element={<Navigate to="/customers?setup=new" replace />} />
          {/* Fix round 1 (Task 24 review, Important): a plain redirect target
              for the wizard's own page tour (`page-setup-wizard`,
              `tours/pages/setup.ts`), which needs a real, parameter-free
              `launchPath` the Help Centre's "Open & tour" can navigate to
              (spec 09 §5). The tour's own `route` stays a documented
              sentinel (`/customers/setup-wizard`, never matched by real
              navigation elsewhere) purely so it has a unique page-tour key —
              this redirect is what makes that same string ALSO a real,
              working link into the modal, exactly like the
              `provisioning/new-customer` redirect above. */}
          <Route path="customers/setup-wizard" element={<Navigate to="/customers?setup=new" replace />} />
          <Route path="provisioning/:id" element={<ProvisioningRunDetailPage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="templates/new" element={<TemplateWizardPage />} />
          <Route path="templates/:id/edit" element={<TemplateWizardPage />} />
          <Route path="templates/:id" element={<TemplateDetailPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
